import * as THREE from 'https://unpkg.com/three@0.160.0/build/three.module.js';
import { Water } from 'https://unpkg.com/three@0.160.0/examples/jsm/objects/Water.js';

// ============ RENDERER ============
const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.55;
renderer.domElement.style.cssText = `
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
  background: #041e2e;
  will-change: transform;
`;
document.body.insertBefore(renderer.domElement, document.body.firstChild);

// Full-screen overlay — above the WebGL canvas (z 0), below page content
// (z 10). Created here in JS since it isn't present in the Liquid markup.
// In the Bottom Reels zone this paints the bottom ~45% of the screen solid
// deep ocean blue (#041e2e) with a soft waterline blend above it. A DOM
// gradient is used because the 50000-unit water plane covers everything
// below the horizon in 3D — scene.background alone can never show there.
// Its opacity is driven in animate() from the smoothed camera tilt.
// The gradient is FIXED on a 250vh-tall element that gets TRANSLATED to
// slide the waterline band — rebuilding the gradient string per frame
// repaints the whole viewport every frame and stutters on large screens.
// Band geometry: the blend starts 100vh from the element top; the animate
// loop sets translateY((blendStart - 100)vh).
const underwaterOverlay = document.createElement('div');
underwaterOverlay.style.cssText = `
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 250vh;
  z-index: 1;
  pointer-events: none;
  opacity: 0;
  transform: translateY(-58vh);
  will-change: transform, opacity;
  background: linear-gradient(
    to bottom,
    rgba(10, 62, 88, 0) 40%,
    rgba(10, 58, 84, 0.3) 42.4%,
    rgba(8, 50, 74, 0.65) 44.4%,
    rgba(6, 40, 60, 0.92) 46.4%,
    rgb(4, 26, 42) 51.2%,
    rgb(2, 12, 22) 64%
  );
`;
document.body.appendChild(underwaterOverlay);

// Animated light refractions over the deep-blue zone, rendered with a real
// GLSL caustics shader (classic interference pattern) via a second small
// Three.js renderer on the [data-caustics] canvas. The canvas is re-parented
// to <body> so it stacks ABOVE the depth gradient (z 2), below content (z 10).
const causticsCanvas = document.querySelector('[data-caustics]');
let causticsRenderer = null;
let causticsScene = null;
let causticsCamera = null;
let causticsUniforms = null;

if (causticsCanvas) {
  document.body.appendChild(causticsCanvas);
  causticsCanvas.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
    pointer-events: none;
    opacity: 0;
    will-change: opacity;
  `;

  causticsRenderer = new THREE.WebGLRenderer({
    canvas: causticsCanvas,
    alpha: true,
    antialias: false,
  });
  causticsRenderer.setClearColor(0x000000, 0);
  causticsRenderer.setPixelRatio(1); // pattern is soft — full DPR not needed

  causticsUniforms = {
    uTime: { value: 0 },
    uAspect: { value: window.innerWidth / window.innerHeight },
    // vUv.y (1 = screen top) where the underwater blend begins. 0.56 for the
    // bottomReels view (44% from top); slides to 0.82 (18% from top) for the
    // trapHaulers deep view. Driven per-frame in animate().
    uRegionTop: { value: 0.56 },
    // 0 → 1 as the trapHaulers deep view completes: the sandy ocean floor
    // (with seaweed on the left) rises into the bottom of the frame.
    uFloorT: { value: 0 },
    // When the photo floor layer is active, this drives extra animated
    // caustic light over the sand band so the static image feels alive.
    uFloorGlow: { value: 0 },
    // vUv.y of the sand region's top edge — rises during the footer stage
    // so the warm sand light covers ALL the exposed sand, not just the
    // bottom sliver of the screen.
    uFloorTop: { value: 0.17 },
    // Scroll-follow shifts (vUv units): the caustic patterns sample at an
    // offset matching the scene's motion so the light webs TRAVEL WITH the
    // water/sand instead of staying glued to the screen like an overlay.
    uPatternShift: { value: 0 },
    uFloorShift: { value: 0 },
  };

  const causticsMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: causticsUniforms,
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0.0, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      varying vec2 vUv;
      uniform float uTime;
      uniform float uAspect;
      uniform float uRegionTop;
      uniform float uFloorT;
      uniform float uFloorGlow;
      uniform float uFloorTop;
      uniform float uPatternShift;
      uniform float uFloorShift;

      // Classic water-caustic interference pattern
      float caustic(vec2 uv, float t) {
        vec2 p = mod(uv * 6.28318530718, 6.28318530718) - 250.0;
        vec2 i = p;
        float c = 1.0;
        float inten = 0.005;
        for (int n = 0; n < 5; n++) {
          float tt = t * (1.0 - (3.5 / float(n + 1)));
          i = p + vec2(cos(tt - i.x) + sin(tt + i.y),
                       sin(tt - i.y) + cos(tt + i.x));
          c += 1.0 / length(vec2(p.x / (sin(i.x + tt) / inten),
                                 p.y / (cos(i.y + tt) / inten)));
        }
        c /= 5.0;
        c = 1.17 - pow(c, 1.4);
        return pow(abs(c), 8.0);
      }

      void main() {
        // Fades in across the same blend band as the depth gradient —
        // anchored to uRegionTop so the whole region slides up the screen
        // during the trapHaulers transition
        float inRegion = smoothstep(uRegionTop, uRegionTop - 0.16, vUv.y);
        if (inRegion <= 0.001) discard;

        // 0 at the top of the blend -> 1 at the bottom of the screen
        float depthRef = uRegionTop + 0.02;
        float depth = clamp((depthRef - vUv.y) / depthRef, 0.0, 1.0);

        // Lower scale = larger, lazier light webs; lower time factor = slow
        // drift. The y sample is shifted by the waterline's scroll motion so
        // the pattern rides with the water, not the screen.
        vec2 uv = vec2(vUv.x * uAspect, vUv.y - uPatternShift) * 0.8;
        float c = caustic(uv, uTime * 0.12);

        // Dimmer and cooler with depth — light dies as it goes down
        float brightness = c * 0.18 * (1.0 - depth * 0.85) * inRegion;

        // Sun-through-waves shimmer: a horizontal band just under the
        // waterline where light ripples sideways at wave speed, tying the
        // underwater glow to the moving surface above it.
        float bandTop = smoothstep(uRegionTop, uRegionTop - 0.06, vUv.y);
        float bandBottom = smoothstep(uRegionTop - 0.18, uRegionTop - 0.10, vUv.y);
        float surfBand = bandTop * bandBottom * inRegion;
        float wave = 0.5 + 0.5 * sin(vUv.x * uAspect * 22.0 + uTime * 0.9)
                               * sin(vUv.x * uAspect * 8.0 - uTime * 0.55);
        brightness += surfBand * wave * 0.09;

        vec3 col = mix(vec3(0.45, 0.85, 0.95), vec3(0.15, 0.5, 0.75), depth);

        // Animated caustic light dancing across the photo floor layer: the
        // normal caustics fade to near-nothing at the bottom of the screen,
        // so the sand band gets its own boosted pass. Warm-tinted so it
        // reads as sunlight on sand rather than blue glow.
        if (uFloorGlow > 0.001) {
          float floorBand = smoothstep(uFloorTop, uFloorTop - 0.08, vUv.y);
          // Sand-light pattern rides with the floor layer's own motion
          float fc = caustic(vec2(vUv.x * uAspect, vUv.y - uFloorShift) * 1.1, uTime * 0.1);
          brightness += fc * 0.30 * floorBand * uFloorGlow;
          // Warm sandy sunlight — light reflecting off sand, not blue water
          col = mix(col, vec3(0.95, 0.89, 0.70), floorBand * 0.85);
        }

        // ---- Ocean floor: low sandy bottom + seaweed (left), rises with
        // uFloorT. Kept subtle and desaturated — murky silhouettes in deep
        // water, not bright graphic shapes. ----
        if (uFloorT > 0.001) {
          // Low undulating sand line (~12% of the screen — enough to stay
          // visible above the footer at full scroll), sliding up
          float sandTop = mix(-0.20, 0.12, uFloorT)
            + sin(vUv.x * uAspect * 5.0) * 0.010
            + sin(vUv.x * uAspect * 13.0 + 2.0) * 0.005;

          // Seaweed — short dark silhouettes anchored to the sand on the
          // left, swaying on a slow current
          for (int i = 0; i < 6; i++) {
            float fi = float(i);
            float ax = 0.035 + fi * 0.038 + sin(fi * 7.3) * 0.012; // left cluster
            float h = 0.11 + 0.06 * sin(fi * 12.7 + 1.0);          // blade height
            float baseY = sandTop - 0.02;
            float t = (vUv.y - baseY) / max(h * uFloorT, 0.001);
            if (t > 0.0 && t < 1.0) {
              float sway = sin(uTime * 0.5 + fi * 1.7 + t * 3.2) * 0.009 * t
                         + sin(uTime * 0.22 + fi) * 0.012 * t * t;
              float bladeX = ax + sway;
              float w = 0.005 * (1.0 - t * 0.72); // taper toward the tip
              float blade = smoothstep(w, w * 0.1, abs(vUv.x - bladeX));
              if (blade > 0.0) {
                vec3 weed = mix(vec3(0.04, 0.16, 0.10), vec3(0.07, 0.26, 0.13), t);
                weed += vec3(0.20, 0.35, 0.30) * c * 0.25; // faint caustic light
                weed = mix(weed, vec3(0.06, 0.16, 0.22), 0.45); // water haze
                col = mix(col, weed, blade * 0.8);
                brightness = max(brightness, blade * 0.65 * uFloorT);
              }
            }
          }

          // Sand — dark desaturated khaki fading hazily into the water,
          // faint caustic light and a whisper of ripple texture
          if (vUv.y < sandTop) {
            float dSand = clamp((sandTop - vUv.y) / max(sandTop + 0.05, 0.001), 0.0, 1.0);
            vec3 sand = vec3(0.50, 0.44, 0.33);
            sand *= 1.0 - dSand * 0.45;
            sand = mix(sand, vec3(0.09, 0.20, 0.28), 0.50);
            sand += vec3(0.35, 0.50, 0.50) * c * 0.28 * (1.0 - dSand * 0.7);
            sand *= 0.975 + 0.025 * sin(vUv.x * uAspect * 90.0 + vUv.y * 40.0);
            float sandEdge = smoothstep(sandTop, sandTop - 0.03, vUv.y);
            col = mix(col, sand, sandEdge);
            brightness = mix(brightness, 0.9 * uFloorT, sandEdge);
          }
        }

        gl_FragColor = vec4(col, brightness);
      }
    `,
  });

  causticsScene = new THREE.Scene();
  causticsCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  causticsScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), causticsMaterial));
}

// ============ OCEAN FLOOR (photo layer) ============
// Photoreal sand + seaweed strip (assets/ocean-floor.webp, URL via data tag).
// Slides up from below the frame as the trapHaulers deep view completes,
// sitting above the depth gradient and below the caustics canvas so the
// light webs play across the photo. When the image is missing, the shader's
// procedural floor (uFloorT) takes over as a fallback.
const oceanFloorUrl = document.querySelector('script[data-oceanfloor]')?.dataset.oceanfloor;
let floorLayer = null;
if (oceanFloorUrl) {
  floorLayer = document.createElement('div');
  floorLayer.style.cssText = `
    position: fixed;
    left: 0;
    right: 0;
    bottom: -6vh;
    /* Fixed height — ALL motion is transform-only (GPU). Animating height
       forces layout+repaint of this huge layer every frame and stutters on
       large screens. The card-view crop comes from translating the layer
       down so its lower rows sit below the viewport. */
    height: 72vh;
    z-index: 1;
    pointer-events: none;
    opacity: 0;
    transform: translateY(100%);
    will-change: transform, opacity;
    background: url("${oceanFloorUrl}") center top / 100% auto no-repeat;
    -webkit-mask-image: linear-gradient(to bottom, transparent 0%, black 42%);
    mask-image: linear-gradient(to bottom, transparent 0%, black 42%);
  `;
  // Depth tint over the layer's upper region: as the footer stage reveals
  // more of the image upward, this darkens the image's own water toward the
  // page's deep-ocean colors so the reveal reads as continuous deep water,
  // not a detached backdrop. Opacity driven per-frame (0 until the stage).
  const floorTint = document.createElement('div');
  floorTint.style.cssText = `
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: 0;
    background: linear-gradient(
      to bottom,
      rgb(2, 12, 22) 0%,
      rgba(3, 20, 33, 0.85) 30%,
      rgba(5, 30, 46, 0.35) 51%,
      transparent 68%
    );
  `;
  floorLayer.appendChild(floorTint);
  floorLayer.tintEl = floorTint;
  document.body.appendChild(floorLayer);
}

function sizeCausticsCanvas() {
  if (!causticsRenderer) return;
  causticsRenderer.setSize(window.innerWidth, window.innerHeight);
  causticsUniforms.uAspect.value = window.innerWidth / window.innerHeight;
}
sizeCausticsCanvas();
window.addEventListener('resize', sizeCausticsCanvas);

// ============ SCENE + CAMERA ============
const scene = new THREE.Scene();
// Atmospheric perspective: distant water fades toward a pale horizon haze
// that matches the bottom of the sky gradient, so ocean melts into sky
// instead of ending at a hard line. (Background/sky itself is not fogged.)
scene.fog = new THREE.FogExp2(0xa3cbe2, 0.00018);
const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 1, 20000);
// Camera sits very low — near water level
camera.position.set(0, 4, 80);
// Look straight at the horizon — perfectly level
camera.lookAt(0, 2, -600);

// ============ SKY ============
// The procedural Sky shader is gone — its tone-mapping interplay kept
// rendering grey/white. The sky is now a simple canvas gradient texture:
// deep blue at the top fading to pale blue at the horizon. It's used both
// as the background and as scene.environment so reflections pick up blue.
const skyCanvas = document.createElement('canvas');
skyCanvas.width = 2;
skyCanvas.height = 512;
const skyCtx = skyCanvas.getContext('2d');
// The camera's visible sky band samples roughly the 0.33-0.55 stop range
// of this gradient (equirect mapping, 60° FOV near the horizon) — the
// stops there are what set the on-screen sky color. Deep azure dominates;
// pale haze is confined to right at the horizon line.
const skyGrad = skyCtx.createLinearGradient(0, 0, 0, 512);
skyGrad.addColorStop(0, '#1a5ea8');
skyGrad.addColorStop(0.35, '#3579bd');
skyGrad.addColorStop(0.5, '#5b9ed4');
skyGrad.addColorStop(0.68, '#8fc2e4');
skyGrad.addColorStop(1, '#c8e6f4');
skyCtx.fillStyle = skyGrad;
skyCtx.fillRect(0, 0, 2, 512);

const skyTexture = new THREE.CanvasTexture(skyCanvas);
skyTexture.mapping = THREE.EquirectangularReflectionMapping;
scene.background = skyTexture;
scene.environment = skyTexture;

// Sun direction — still drives the water's specular glint and the
// directional light even though the Sky shader is gone.
const sun = new THREE.Vector3();
const phi = THREE.MathUtils.degToRad(45);
const theta = THREE.MathUtils.degToRad(185);
sun.setFromSphericalCoords(1, phi, theta);

// ============ WATER ============
const waterGeometry = new THREE.PlaneGeometry(50000, 50000, 8, 8);

// Get waternormals URL from the data tag in the page
const waternormalsUrl = document.querySelector('script[data-waternormals]')?.dataset.waternormals
  || 'https://threejs.org/examples/textures/waternormals.jpg';

const water = new Water(waterGeometry, {
  textureWidth: 512,
  textureHeight: 512,
  waterNormals: new THREE.TextureLoader().load(waternormalsUrl, (texture) => {
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  }),
  sunDirection: new THREE.Vector3(sun.x, sun.y, sun.z).normalize(),
  sunColor: 0xffffff,
  waterColor: 0x0b4d7a, // rich ocean blue — deep but clearly blue, not black
  distortionScale: 3.5,
  alpha: 1.0,
});
water.rotation.x = -Math.PI / 2;
water.position.y = 0;
// The Water shader has no reflection-strength uniform — its base
// reflectivity is the rf0 constant in the fragment shader (0.3 in r160).
// Lowering it lets the deep waterColor show through instead of mirrored sky.
water.material.onBeforeCompile = (shader) => {
  shader.fragmentShader = shader.fragmentShader.replace(
    'float rf0 = 0.3;',
    'float rf0 = 0.12;'
  );
};
// Finer wave pattern — reads as real ocean texture from the low camera
// instead of broad soft swells.
water.material.uniforms['size'].value = 3.0;
scene.add(water);

// ============ UNDERWATER BACKDROP PLANES ============
// Deep ocean floor color far below the surface, plus a mid-depth tint, so the
// view below the waterline reads as deep ocean rather than blank.
const underwaterBgGeometry = new THREE.PlaneGeometry(50000, 50000);
const underwaterBgMaterial = new THREE.MeshBasicMaterial({
  color: 0x041e2e,
  side: THREE.DoubleSide,
});
const underwaterBg = new THREE.Mesh(underwaterBgGeometry, underwaterBgMaterial);
underwaterBg.rotation.x = -Math.PI / 2;
underwaterBg.position.y = -100; // far below water surface
scene.add(underwaterBg);

const midwaterGeometry = new THREE.PlaneGeometry(50000, 50000);
const midwaterMaterial = new THREE.MeshBasicMaterial({
  color: 0x062840,
  transparent: true,
  opacity: 0.8,
  side: THREE.DoubleSide,
});
const midwaterBg = new THREE.Mesh(midwaterGeometry, midwaterMaterial);
midwaterBg.rotation.x = -Math.PI / 2;
midwaterBg.position.y = -20;
scene.add(midwaterBg);

// ============ CLOUDS ============
function createCloudTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');

  // Soft puff, centered slightly high so the underside falls off sooner
  const gradient = ctx.createRadialGradient(128, 110, 0, 128, 118, 126);
  gradient.addColorStop(0, 'rgba(255,255,255,0.95)');
  gradient.addColorStop(0.3, 'rgba(245,248,252,0.8)');
  gradient.addColorStop(0.6, 'rgba(220,230,240,0.4)');
  gradient.addColorStop(1, 'rgba(200,215,230,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 256, 256);

  // Sunlit-from-above shading: tint only the drawn puff pixels with a
  // grey-blue gradient toward the bottom — bright top, shaded underside.
  // Neutral greys (not blue-greys) for stronger white/grey contrast
  const shade = ctx.createLinearGradient(0, 70, 0, 256);
  shade.addColorStop(0, 'rgba(170,175,185,0)');
  shade.addColorStop(0.5, 'rgba(150,155,165,0.42)');
  shade.addColorStop(1, 'rgba(125,131,142,0.75)');
  ctx.globalCompositeOperation = 'source-atop';
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, 256, 256);
  ctx.globalCompositeOperation = 'source-over';

  return new THREE.CanvasTexture(canvas);
}

const cloudTexture = createCloudTexture();
const clouds = [];
const cloudCount = 23; // enough for a living sky while keeping it mostly blue

for (let i = 0; i < cloudCount; i++) {
  // Three depth bands for real parallax. Near clouds are big, high in the
  // frame and drift fast; far clouds are small, hug the horizon, drift slow
  // and pick up atmospheric haze from the fog.
  //
  // CRITICAL SCALING RULE: both the y range AND the horizontal spread must
  // scale with each band's distance. The camera frustum is a wedge — at the
  // near band's depth (~500 units) the visible strip is only ~±900 units
  // wide even on an ultrawide screen. If near clouds spawn across ±6000,
  // they are almost never on screen and the sky looks sparse and static.
  // spreadHalf keeps each band mostly inside view at any aspect ratio;
  // wrapMargin is just enough off-screen travel to avoid visible pop-in.
  const band = Math.random();
  let zPos, sizeBase, sizeVar, yMin, yMax, speedMul, clusterScale, spreadHalf, wrapMargin;
  if (band < 0.25) {
    // Near — semi-large, strong parallax. Kept small enough (and pushed a
    // little further out) that one cloud can never whitewash the screen.
    zPos = -400 - Math.random() * 500;
    sizeBase = 190; sizeVar = 170;
    yMin = 70; yMax = 260;
    speedMul = 1.8;
    clusterScale = 1.5;
    spreadHalf = 1400; wrapMargin = 600;
  } else if (band < 0.65) {
    // Mid
    zPos = -800 - Math.random() * 1200;
    sizeBase = 220; sizeVar = 280;
    yMin = 100; yMax = 420;
    speedMul = 1.3;
    clusterScale = 1.0;
    spreadHalf = 3000; wrapMargin = 500;
  } else {
    // Far — small, near the horizon
    zPos = -2200 - Math.random() * 2300;
    sizeBase = 110; sizeVar = 160;
    yMin = 60; yMax = 300;
    speedMul = 0.5;
    clusterScale = 0.7;
    spreadHalf = 6500; wrapMargin = 300;
  }

  // 3-7 overlapping sprites — small wisps up to full cumulus
  const puffCount = 3 + Math.floor(Math.random() * 5);
  const cloudGroup = new THREE.Group();

  // Per-group opacity 0.35–0.8 — some clouds thin and wispy, others fuller
  const groupOpacity = 0.35 + Math.random() * 0.45;
  // Per-group shape: flat stretched stratus (0.3) up to puffier cumulus (0.6)
  const puffiness = 0.3 + Math.random() * 0.3;
  // Per-group width of the puff cluster — tight clumps vs long streaks
  const clusterWidth = (220 + Math.random() * 260) * clusterScale;

  // Dense clouds get slightly translucent puffs so stacked alpha can't
  // pile up into a flat white mass.
  const stackFactor = 1 / Math.sqrt(puffCount / 3);

  for (let p = 0; p < puffCount; p++) {
    // Vertical position within the cloud decides shading AND shape: puffs
    // near the cloud base get a grey-blue tint (self-shadowed) and flatten
    // out wide, top puffs stay bright white and domed — the classic
    // cumulus silhouette (rounded sunlit crown, flat shadowed belly).
    const puffY = (Math.random() - 0.5) * 60;
    const shadeT = Math.min(Math.max(0.5 - puffY / 60, 0), 1);
    const tint = new THREE.Color(1, 1, 1)
      .lerp(new THREE.Color(0.56, 0.59, 0.65), shadeT * 0.85);

    const spriteMaterial = new THREE.SpriteMaterial({
      map: cloudTexture,
      color: tint,
      transparent: true,
      opacity: groupOpacity * (0.85 + Math.random() * 0.3) * stackFactor,
      depthWrite: false,
    });

    const sprite = new THREE.Sprite(spriteMaterial);
    const size = sizeBase + Math.random() * sizeVar;
    // Base puffs stretch wider and flatter; crown puffs stay rounder
    const widen = 1 + shadeT * 0.45;
    const flatten = 1 - shadeT * 0.35;
    sprite.scale.set(size * widen, size * puffiness * flatten, 1);
    sprite.position.set(
      (Math.random() - 0.5) * clusterWidth,
      puffY,
      (Math.random() - 0.5) * 80
    );
    // Morph data — each puff slowly swells/stretches out of phase with its
    // neighbors so the cloud's silhouette changes as it drifts.
    sprite.userData.baseW = size * widen;
    sprite.userData.baseH = size * puffiness * flatten;
    sprite.userData.phase = Math.random() * Math.PI * 2;
    sprite.userData.morphAmp = 0.05 + Math.random() * 0.07;
    cloudGroup.add(sprite);
  }

  cloudGroup.position.set(
    (Math.random() - 0.5) * 2 * spreadHalf,
    yMin + Math.random() * (yMax - yMin),
    zPos
  );

  // World units per second — applied with real delta time in updateClouds,
  // so drift speed is identical on 60Hz, 120Hz, or throttled displays.
  cloudGroup.userData.speed = (18 + Math.random() * 14) * speedMul;
  cloudGroup.userData.wrapX = spreadHalf + wrapMargin;
  cloudGroup.userData.baseOpacity = groupOpacity;

  scene.add(cloudGroup);
  clouds.push(cloudGroup);
}

// Drift + slow shape morphing, driven from the animate loop.
// dt is real seconds since the last rendered frame.
function updateClouds(dt, sceneTime) {
  clouds.forEach(cloud => {
    cloud.position.x += cloud.userData.speed * dt;
    // Wrap within this cloud's own band bounds (left re-entry, no pop-in)
    if (cloud.position.x > cloud.userData.wrapX) {
      cloud.position.x = -cloud.userData.wrapX;
    }
    // Each puff breathes on its own slow cycle (different x/y phases so the
    // silhouette stretches and squashes rather than just pulsing in size).
    cloud.children.forEach(sprite => {
      const d = sprite.userData;
      if (!d.baseW) return;
      const sx = 1 + d.morphAmp * Math.sin(sceneTime * 0.07 + d.phase);
      const sy = 1 + d.morphAmp * Math.sin(sceneTime * 0.055 + d.phase * 1.7);
      sprite.scale.set(d.baseW * sx, d.baseH * sy, 1);
    });
  });
}

// ============ WATERLINE ============
// Subtle horizontal glow at y=0; opacity is driven in animate() so it only
// shows when the camera is near the surface.
const waterlineMat = new THREE.MeshBasicMaterial({
  color: 0x44aacc,
  transparent: true,
  opacity: 0.12,
  depthWrite: false,
  side: THREE.DoubleSide,
});
const waterlineGeo = new THREE.PlaneGeometry(10000, 3);
const waterlineMesh = new THREE.Mesh(waterlineGeo, waterlineMat);
waterlineMesh.rotation.x = -Math.PI / 2;
waterlineMesh.position.y = 0.2;
scene.add(waterlineMesh);

// ============ LIGHTING ============
const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
scene.add(ambientLight);

const dirLight = new THREE.DirectionalLight(0xfff4e0, 1.5);
dirLight.position.set(sun.x * 100, sun.y * 100, sun.z * 100);
scene.add(dirLight);

// ============ PERFORMANCE MODE ============
// SINGLE TIME SOURCE: the rAF timestamp. sceneNowSec is updated once per
// rendered frame in animate() and every time-based system reads from it —
// mixing performance.now()/THREE.Clock with the rAF timestamp produces
// inconsistent deltas that stutter even at a stable framerate.
let sceneNowSec = 0;

// Read initial state — check both possible stored values
const storedPerf = localStorage.getItem('waterman_perf');
let performanceMode = storedPerf === 'true' || storedPerf === 'on';
let pausedAt = 0;
let pausedTime = 0;

function setPerformanceMode(enabled) {
  if (enabled && !performanceMode) {
    pausedAt = sceneNowSec;
    performanceMode = true;
  } else if (!enabled && performanceMode) {
    pausedTime += sceneNowSec - pausedAt;
    performanceMode = false;
  }
  // Write both keys so everything stays in sync
  localStorage.setItem('waterman_perf', String(enabled));

  // Update button visual state
  const btn = document.querySelector('[data-perf-toggle]');
  if (btn) {
    btn.setAttribute('aria-pressed', String(enabled));
    // Toggle active class for visual feedback
    btn.classList.toggle('active', enabled);
  }

  // Apply or remove performance class on body
  document.body.classList.toggle('performance-mode', enabled);
}

function getSceneTime() {
  if (performanceMode) return pausedAt - pausedTime;
  return sceneNowSec - pausedTime;
}

// Wire up the button — use click not change
// Run after DOM is ready
function initPerfToggle() {
  const btn = document.querySelector('[data-perf-toggle]');
  if (btn) {
    // Set initial visual state
    btn.setAttribute('aria-pressed', String(performanceMode));
    btn.classList.toggle('active', performanceMode);

    btn.addEventListener('click', () => {
      const next = !performanceMode;
      setPerformanceMode(next);
      console.log('[scene] Performance mode:', next);
    });
    console.log('[scene] Performance toggle wired up:', btn.outerHTML);
  } else {
    console.warn('[scene] Performance toggle button not found — retrying in 1s');
    // Retry once in case header renders after scene.js
    setTimeout(initPerfToggle, 1000);
  }
}

// Also listen for custom event from other theme JS
document.addEventListener('waterman:performanceMode', (e) => {
  setPerformanceMode(e.detail.enabled);
});

// Init after DOM loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPerfToggle);
} else {
  initPerfToggle();
}

// Apply initial performance mode state on load
if (performanceMode) {
  document.body.classList.add('performance-mode');
}

// ============ SCROLL CAMERA SYSTEM ============
// Three camera positions, ALL above the water. The scroll listener writes
// target values only; the animate loop smoothly chases them and is the only
// code that touches the camera. Targets are a pure function of scrollY, so
// scrolling up reverses every transition automatically.
//
//   Zone 0 (0 → 1vh):   hero → parasail lerp
//   Zone 1 (at 1vh):    parasail — y: 2, lookAt(0, 0, -500)
//   Zone 2 (0.8 → 1.5vh): parasail → bottomReels lerp. Camera height stays
//                       at y: 2 and the view tilts DOWN (~13°) so the horizon
//                       rises to ~30% from the top.
//   Zone 3 (2.3 → 3vh): bottomReels → trapHaulers lerp. The view tilts down
//                       further (~19°) so sky + waterline compress into the
//                       top ~25% of the screen and the underwater view fills
//                       the rest. The overlay gradient and caustics region
//                       slide up in lockstep (see animate()).
//   >= 3vh:             hard lock at trapHaulers forever. The camera never
//                       goes below y: 2 and never below the water.
//
// lookY values are large negatives because horizon placement is set by the
// tilt ANGLE: atan(65/280) ≈ 13° → horizon ~30% from top; atan(97/280) ≈ 19°
// → horizon ~20% from top (60° FOV). Small lookY tweaks like -3 do nothing.
const CAM = {
  hero:        { y: 4, lookY: 2,   lookZ: -500 },
  parasail:    { y: 2, lookY: 0,   lookZ: -500 },
  bottomReels: { y: 2, lookY: -63, lookZ: -200 },
  trapHaulers: { y: 2, lookY: -95, lookZ: -200 },
  // Footer stage — tilt so steep the horizon leaves the top of the screen:
  // sky scrolls off, the seafloor takes over, and the footer content sits
  // on the sand. Engaged by proximity to the page bottom (see below).
  seafloor:    { y: 2, lookY: -170, lookZ: -200 },
};

function lerp(a, b, t) {
  return a + (b - a) * Math.min(Math.max(t, 0), 1);
}

// Target values (written by the scroll listener) and smoothed current values
// (chased by the animate loop at zp.smooth per frame, then applied to the
// camera). The scroll listener never touches the camera directly.
let targetCamY = CAM.hero.y;
let targetLookY = CAM.hero.lookY;
let targetLookZ = CAM.hero.lookZ;
let currentCamY = CAM.hero.y;
let currentLookY = CAM.hero.lookY;
let currentLookZ = CAM.hero.lookZ;

// Transition windows + smoothing, sized to the screen. On small screens
// 100vh is physically short, so the desktop windows compress into a couple
// of centimeters of thumb-scroll and feel rushed — there the windows are
// stretched and the per-frame smoothing is softer (more glide).
function zoneParams() {
  const small = window.innerWidth < 768 || window.innerHeight < 700;
  return small
    ? { z0Len: 0.85, z2Start: 0.7,  z2Len: 1.0, z3Start: 2.0,  z3Len: 0.85,
        z4Start: 3.1,  z4Len: 0.85, smooth: 0.055 }
    : { z0Len: 0.6,  z2Start: 0.8,  z2Len: 0.7, z3Start: 2.1,  z3Len: 0.6,
        z4Start: 3.25, z4Len: 0.7,  smooth: 0.08 };
}
let zp = zoneParams();

// Cached document height — reading scrollHeight forces a synchronous layout,
// which MUST NOT happen inside the scroll listener (main-thread jank).
// Refreshed on resize/load and shortly after load (late images/fonts).
let docHeightCached = 0;
function refreshMetrics() {
  zp = zoneParams();
  docHeightCached = document.documentElement.scrollHeight;
}

// Timestamp of the last real scroll event (performance.now — same timebase
// as the rAF timestamp). Drives the adaptive lerp in animate().
let lastScrollAt = -1e9;

let scrollY = window.scrollY;

// Floor progress: targetFloorP is scroll-native (set in the scroll
// listener); floorPSm chases it with the camera's dt-corrected smoothing.
// MUST be declared before updateCameraFromScroll's initial call below.
let targetFloorP = 0;
let floorPSm = 0;

// Product pages pin the camera to one named CAM view for the whole page —
// the background holds the same composition as that product's homepage card
// instead of scrolling. Set via data-scene-lock="<CAM key>" on the page
// container (e.g. templates/page.bottom-reels.liquid).
const sceneLockEl = document.querySelector('[data-scene-lock]');
const sceneLock = sceneLockEl && CAM[sceneLockEl.dataset.sceneLock]
  ? sceneLockEl.dataset.sceneLock
  : null;

// SCROLL PATH — the listener does target math ONLY: no layout reads (doc
// height is cached), no style writes, no canvas work. Everything visual
// happens in the animate loop.
function updateCameraFromScroll() {
  if (sceneLock) {
    targetCamY = CAM[sceneLock].y;
    targetLookY = CAM[sceneLock].lookY;
    targetLookZ = CAM[sceneLock].lookZ;
    // Locked pages pin the floor too: full seafloor view, the card-strip
    // view (matches KNEE_P below), or hidden.
    targetFloorP = sceneLock === 'seafloor' ? 1
      : sceneLock === 'trapHaulers' ? 0.78
      : 0;
    return;
  }

  scrollY = window.scrollY;
  const vh = window.innerHeight;

  if (scrollY < vh * zp.z2Start) {
    // Zone 0 — hero → parasail. Completes early in the first viewport so the
    // transition responds right away; the camera then rests at parasail.
    const t = scrollY / (vh * zp.z0Len);
    targetCamY = lerp(CAM.hero.y, CAM.parasail.y, t);
    targetLookY = lerp(CAM.hero.lookY, CAM.parasail.lookY, t);
    targetLookZ = lerp(CAM.hero.lookZ, CAM.parasail.lookZ, t);
  } else if (scrollY < vh * zp.z3Start) {
    // Zone 2 — parasail → bottomReels (view tilts down, horizon moves up).
    // The boundary equals zone 3's start so targets stay CONTINUOUS across
    // it — a mismatch here makes the camera jump mid-transition.
    const t = (scrollY - vh * zp.z2Start) / (vh * zp.z2Len);
    targetCamY = lerp(CAM.parasail.y, CAM.bottomReels.y, t);
    targetLookY = lerp(CAM.parasail.lookY, CAM.bottomReels.lookY, t);
    targetLookZ = lerp(CAM.parasail.lookZ, CAM.bottomReels.lookZ, t);
  } else {
    // Zone 3 — bottomReels → trapHaulers; settles before the Trap Haulers
    // card is fully in view. lerp clamps t, so it then rests at trapHaulers.
    const t = (scrollY - vh * zp.z3Start) / (vh * zp.z3Len);
    targetCamY = lerp(CAM.bottomReels.y, CAM.trapHaulers.y, t);
    targetLookY = lerp(CAM.bottomReels.lookY, CAM.trapHaulers.lookY, t);
    targetLookZ = lerp(CAM.bottomReels.lookZ, CAM.trapHaulers.lookZ, t);

    // Zone 4 — trapHaulers → seafloor. Ends at the ACTUAL page bottom
    // (document height varies with footer size and screen), so the stage
    // completes exactly as scrolling runs out — a fixed end position either
    // stalls short of the bottom or locks early, both of which read jumpy.
    const z4StartPx = vh * zp.z4Start;
    const z4EndPx = Math.max(
      docHeightCached - vh,
      z4StartPx + vh * 0.3
    );
    const t4 = Math.min(Math.max((scrollY - z4StartPx) / (z4EndPx - z4StartPx), 0), 1);
    if (t4 > 0) {
      targetLookY = lerp(CAM.trapHaulers.lookY, CAM.seafloor.lookY, t4);
      targetLookZ = lerp(CAM.trapHaulers.lookZ, CAM.seafloor.lookZ, t4);
    }
  }

  // ---- FLOOR PROGRESS: scroll-native, continuous two-slope ramp ----
  // The floor is NOT derived from the camera tilt (that gave it the camera's
  // rest-plateaus — burst / freeze / burst — and burst speeds of ~145x the
  // scroll rate, which read as jumpy snapping no matter how smoothed).
  // It moves like page content, linearly with scrollY: a slightly faster
  // first slope so it REACHES the approved card-strip height (P=0.667,
  // offset 24vh) right as the Trap Haulers card arrives at the knee, then
  // a gentler slope through to the MEASURED page bottom. Both slopes are
  // constant and meet continuously — no plateaus, no bursts, any screen.
  const floorStartPx = vh * (zp.z3Start + 0.1);
  const kneePx = vh * (zp.z4Start - 0.25);
  const floorEndPx = Math.max(
    docHeightCached - vh,
    kneePx + vh * 0.4
  );
  const KNEE_P = 0.78; // the card-strip floor position (offset ~16vh)
  if (scrollY <= kneePx) {
    targetFloorP = KNEE_P * Math.min(Math.max(
      (scrollY - floorStartPx) / (kneePx - floorStartPx), 0), 1);
  } else {
    targetFloorP = KNEE_P + (1 - KNEE_P) * Math.min(Math.max(
      (scrollY - kneePx) / (floorEndPx - kneePx), 0), 1);
  }
}

// ============ CUSTOM SMOOTH SCROLL (wheel + keyboard inertia) ============
// A mouse wheel click moves the page ~100px in ONE jump — no lerp downstream
// can hide a stepped input. So wheel/keyboard scrolling is virtualized with
// MOMENTUM PHYSICS: inputs add velocity impulses; the animate loop
// integrates position and decays velocity by friction (Lenis-class feel).
// Deliberately NOT hijacked: touch (native momentum is better than
// synthetic), pinch-zoom (ctrl+wheel), horizontal wheel, scrollbar drags
// (resynced below), and reduced-motion users.
const prefersReducedMotion =
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;
let virtualScroll = false; // engages on first hijacked input
let currentScrollY = window.scrollY;
// MOMENTUM PHYSICS (px/sec). Wheel/keys add velocity impulses; each frame
// integrates position from velocity and decays velocity by friction. This
// beats target-lerp because rapid inputs ACCUMULATE momentum and reversals
// fight existing velocity — like an object with mass.
let scrollVel = 0;
// Friction per 60fps frame (applied as pow(f, dt*60) — frame-rate
// independent). 0.918 → velocity time constant ≈195ms, ~99% settled in
// ~0.9s: the long organic tail. Trackpads get 0.86 (τ≈110ms) so the page
// tracks the fingers instead of floating on double-momentum.
const FRICTION_WHEEL = 0.918;
const FRICTION_TRACKPAD = 0.86;
let scrollFriction = FRICTION_WHEEL;
// Impulse gain: travel-per-impulse = gain * τ, so wheel gain 5.6 makes one
// ~100px wheel click glide ~110px total; trackpad 2.4 keeps small frequent
// deltas near 1:1 with the gesture.
const GAIN_WHEEL = 5.6;
const GAIN_TRACKPAD = 2.4;
const MAX_VEL = 6000; // px/s cap — rapid flicks stay fast, never hyperspeed

function maxScrollY() {
  return Math.max(
    0,
    (docHeightCached || document.documentElement.scrollHeight) - window.innerHeight
  );
}

function engageVirtualScroll() {
  if (!virtualScroll) {
    virtualScroll = true;
    currentScrollY = window.scrollY;
    scrollVel = 0;
  }
  lastScrollAt = performance.now();
}

window.addEventListener('wheel', (e) => {
  if (prefersReducedMotion || e.ctrlKey) return; // zoom / a11y — stay native
  if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // horizontal intent
  // Inner scrollable panels (e.g. the distributor results list) marked with
  // data-scroll-native consume the wheel themselves while they can still
  // scroll in that direction; at their edge the page takes over.
  const panel = e.target && e.target.closest
    ? e.target.closest('[data-scroll-native]')
    : null;
  if (panel) {
    const canScroll = e.deltaY > 0
      ? panel.scrollTop + panel.clientHeight < panel.scrollHeight - 1
      : panel.scrollTop > 0;
    if (canScroll) return; // native — the panel scrolls, not the page
  }
  e.preventDefault();
  let dy = e.deltaY;
  if (e.deltaMode === 1) dy *= 33; // lines -> px
  else if (e.deltaMode === 2) dy *= window.innerHeight; // pages -> px
  engageVirtualScroll();
  // Device normalization: mouse wheels send large infrequent deltas
  // (|dy| >= 50, typically 100/120); trackpads send small frequent ones.
  const isTrackpad = Math.abs(dy) < 50 && e.deltaMode === 0;
  scrollFriction = isTrackpad ? FRICTION_TRACKPAD : FRICTION_WHEEL;
  scrollVel += dy * (isTrackpad ? GAIN_TRACKPAD : GAIN_WHEEL);
  scrollVel = Math.min(Math.max(scrollVel, -MAX_VEL), MAX_VEL);
}, { passive: false });

window.addEventListener('keydown', (e) => {
  if (prefersReducedMotion || e.metaKey || e.ctrlKey || e.altKey) return;
  const t = e.target;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA'
    || t.tagName === 'SELECT' || t.tagName === 'BUTTON'
    || t.tagName === 'A' || t.isContentEditable)) return;
  let dy = null;
  if (e.key === 'ArrowDown') dy = 70;
  else if (e.key === 'ArrowUp') dy = -70;
  else if (e.key === 'PageDown') dy = window.innerHeight * 0.85;
  else if (e.key === 'PageUp') dy = -window.innerHeight * 0.85;
  else if (e.key === ' ') dy = window.innerHeight * 0.85 * (e.shiftKey ? -1 : 1);
  else if (e.key === 'Home' || e.key === 'End') {
    e.preventDefault();
    engageVirtualScroll();
    scrollFriction = FRICTION_WHEEL;
    // Exact-glide impulse: v = distance / τ lands right on the edge
    const dest = e.key === 'Home' ? 0 : maxScrollY();
    scrollVel = (dest - currentScrollY) / 0.195;
    return;
  }
  if (dy === null) return;
  e.preventDefault();
  engageVirtualScroll();
  scrollFriction = FRICTION_WHEEL;
  scrollVel += dy * GAIN_WHEEL;
  scrollVel = Math.min(Math.max(scrollVel, -MAX_VEL), MAX_VEL);
}, { passive: false });

// Scroll events still fire — from our own scrollTo, and from sources we
// don't hijack (scrollbar drag, touch, anchor links). External sources must
// RESYNC the virtual positions or the next wheel input would snap the page
// back to a stale target.
window.addEventListener('scroll', () => {
  lastScrollAt = performance.now();
  if (virtualScroll && Math.abs(window.scrollY - currentScrollY) > 1.5) {
    // External scroll source (scrollbar drag, touch, anchor) — resync and
    // kill momentum so the next wheel input starts from reality.
    currentScrollY = window.scrollY;
    scrollVel = 0;
  }
  updateCameraFromScroll();
}, { passive: true });

// Set the correct targets immediately on load, then snap the smoothed values
// to them so a mid-page refresh starts at the right view without a swoop.
refreshMetrics();
updateCameraFromScroll();
currentCamY = targetCamY;
currentLookY = targetLookY;
currentLookZ = targetLookZ;
// Document height settles late (images/fonts) — refresh the cache after load
window.addEventListener('load', refreshMetrics, { passive: true });
setTimeout(refreshMetrics, 1500);
setTimeout(refreshMetrics, 4000);

// ============ ANIMATION LOOP ============
let lastFrame = 0;
// Frame counter for throttled subsystems (clouds every 2nd frame, caustics
// every 3rd) — renderer.render still runs EVERY frame.
let frameIdx = 0;
let cloudDtAcc = 0;
// True once the caustics canvas has at least one rendered frame (see the
// performance-mode static frame logic in animate()).
let causticsHasFrame = false;
// Last rendered uRegionTop / uFloorT — lets performance mode re-render a
// static frame when the waterline region slides or the ocean floor rises.
let causticsLastRegion = -1;
let causticsLastFloor = -1;
// Last WRITTEN style values — styles are only touched when a value actually
// changes, so settled frames do zero style work (compositor-only).
let wOverlayOp = '';
let wOverlayTf = '';
let wCausOp = '';
let wFloorOp = '';
let wFloorTf = '';
let wTintOp = '';

function animate(timestamp) {
  requestAnimationFrame(animate);

  // 15ms (not 16.67): rAF timing wobbles a hair either side of the frame
  // interval — a 16.67ms gate randomly skips legitimate 60Hz frames, which
  // reads as stutter on large-moving layers like the floor.
  if (timestamp - lastFrame < 15) return;
  // Real seconds since last rendered frame; guarded because the very first
  // call has no timestamp. Clamped so a background-tab pause can't jump.
  const dt = Number.isFinite(timestamp) && lastFrame > 0
    ? Math.min((timestamp - lastFrame) / 1000, 0.1)
    : 0.016;
  lastFrame = timestamp || 0;
  frameIdx++;
  // The one and only time source for every animated system this frame
  sceneNowSec = (timestamp || 0) / 1000;

  // --- Scroll momentum physics: integrate position from velocity, decay
  // velocity by friction (frame-rate independent via pow(f, dt*60)). At the
  // page edges velocity is ABSORBED over a few frames (soft-stop) instead
  // of dying instantly — no "thud". True visual overscroll is impossible
  // here: the browser clamps scrollTo, and transforming a page wrapper
  // (the Lenis trick) would break every position:fixed scene layer. ---
  if (virtualScroll && scrollVel !== 0) {
    currentScrollY += scrollVel * dt;
    const maxY = maxScrollY();
    if (currentScrollY <= 0 || currentScrollY >= maxY) {
      currentScrollY = Math.min(Math.max(currentScrollY, 0), maxY);
      scrollVel *= Math.pow(0.55, dt * 60); // boundary soft-stop absorption
    } else {
      scrollVel *= Math.pow(scrollFriction, dt * 60);
    }
    if (Math.abs(scrollVel) < 8) scrollVel = 0; // rest — stop micro-writes
    window.scrollTo(0, currentScrollY);
    updateCameraFromScroll();
  }

  if (!performanceMode) {
    water.material.uniforms['time'].value = getSceneTime() * 0.12;
  }
  // Clouds keep drifting even in performance mode — but only every 2nd
  // frame (23 groups / ~115 sprite updates), with dt accumulated so drift
  // speed is unchanged. Their motion is slow; 30Hz updates are invisible.
  cloudDtAcc += dt;
  if (frameIdx % 2 === 0) {
    updateClouds(cloudDtAcc, sceneNowSec);
    cloudDtAcc = 0;
  }

  // --- Smoothly chase the scroll-driven targets, then apply to the camera.
  // This is the ONLY place the camera is positioned. ---
  // Adaptive + dt-corrected smoothing, tuned to the scroll physics: the
  // camera's chase rate scales with actual scroll VELOCITY — aggressive
  // tracking while the page moves fast, relaxing continuously to a gentle
  // settle as momentum decays (no binary active/idle switch). Native scroll
  // sources (touch/scrollbar) have no virtual velocity, so recent scroll
  // activity provides the same boost as a fallback.
  const velBoost = Math.min(Math.abs(scrollVel) / 1000, 1);
  const timeBoost = (timestamp - lastScrollAt) < 150 ? 1 : 0;
  const baseSmooth = zp.smooth * (1 + 0.9 * Math.max(velBoost, timeBoost));
  const smoothK = 1 - Math.pow(1 - baseSmooth, dt * 60);
  currentCamY = lerp(currentCamY, targetCamY, smoothK);
  currentLookY = lerp(currentLookY, targetLookY, smoothK);
  currentLookZ = lerp(currentLookZ, targetLookZ, smoothK);
  camera.position.y = currentCamY;
  camera.lookAt(0, currentLookY, currentLookZ);

  // --- Deep-blue bottom overlay: fades in with the Bottom Reels tilt ---
  // Derived from the smoothed look target so it eases in/out in lockstep
  // with the camera (0 at parasail, 1 when locked at bottomReels).
  const diveT = Math.min(Math.max(
    (currentLookY - CAM.parasail.lookY) / (CAM.bottomReels.lookY - CAM.parasail.lookY),
    0), 1);
  const overlayOp = diveT.toFixed(3);
  if (overlayOp !== wOverlayOp) {
    underwaterOverlay.style.opacity = overlayOp;
    wOverlayOp = overlayOp;
  }

  // deepT: 0 at the bottomReels view, 1 at the trapHaulers deep view. The
  // waterline blend slides up the screen with it (42% → 16% from the top)
  // so sky + waterline compress into the top ~25% and underwater fills the
  // rest. Derived from the smoothed camera so it reverses on scroll-up.
  const deepT = Math.min(Math.max(
    (currentLookY - CAM.bottomReels.lookY) / (CAM.trapHaulers.lookY - CAM.bottomReels.lookY),
    0), 1);
  // Footer stage progress: 0 at the trapHaulers view, 1 at the seafloor
  // view (page bottom). Sky/waterline exit the top; sand rises.
  const seafloorT = Math.min(Math.max(
    (currentLookY - CAM.trapHaulers.lookY) / (CAM.seafloor.lookY - CAM.trapHaulers.lookY),
    0), 1);
  // Slides from 42vh (bottomReels) to 16vh (trapHaulers) to -22vh (seafloor
  // — fully above the top edge, so the whole screen reads as underwater).
  // Transform-only: the band lives at 100vh inside the tall fixed gradient.
  const blendStart = 42 - 26 * deepT - 38 * seafloorT;
  const overlayTf = 'translateY(' + (blendStart - 100).toFixed(2) + 'vh)';
  if (overlayTf !== wOverlayTf) {
    underwaterOverlay.style.transform = overlayTf;
    wOverlayTf = overlayTf;
  }

  // Light refractions fade in with the depth gradient, and their region
  // follows the sliding waterline. In performance mode we still draw a
  // static frame whenever the region moves (the canvas would otherwise be
  // blank or stuck at the wrong height); it just doesn't animate over time.
  if (causticsRenderer) {
    if (overlayOp !== wCausOp) {
      causticsCanvas.style.opacity = overlayOp;
      wCausOp = overlayOp;
    }
    // Past 1.0 during the footer stage — the caustics region covers the
    // whole screen once the waterline has left the top edge
    const regionTop = 0.56 + 0.26 * deepT + 0.30 * seafloorT;
    causticsUniforms.uRegionTop.value = regionTop;
    // Pattern scroll-follow: main webs ride the waterline band's travel,
    // the sand light rides the floor layer's travel (they move at
    // different rates, so each gets its own shift).
    causticsUniforms.uPatternShift.value = (42 - blendStart) / 100;
    causticsUniforms.uFloorShift.value = 0.72 * floorPSm;
    // Floor: chase the scroll-native progress with the same adaptive
    // dt-corrected smoothing as the camera — uniform glide, no bursts.
    floorPSm = lerp(floorPSm, targetFloorP, smoothK);
    if (floorLayer) {
      // ONE linear motion: 72vh of total travel (rise-in + full reveal) as
      // a single GPU translate. Layer never changes size — no layout/paint.
      const floorOffsetVh = (1 - floorPSm) * 72;
      const floorOp = Math.min(floorPSm * 8, 1).toFixed(3);
      const floorTf = 'translateY(' + floorOffsetVh.toFixed(2) + 'vh)';
      if (floorOp !== wFloorOp) {
        floorLayer.style.opacity = floorOp;
        wFloorOp = floorOp;
      }
      if (floorTf !== wFloorTf) {
        floorLayer.style.transform = floorTf;
        wFloorTf = floorTf;
      }
      // Depth tint: 0.85 while the floor is arriving, easing to full as the
      // top of the photo recedes near the final view. Static gradient —
      // only opacity animates (composited, cheap).
      if (floorLayer.tintEl) {
        const tintRamp = Math.min(Math.max((floorPSm - 0.72) / 0.28, 0), 1);
        const tintOp = (0.85 + 0.15 * tintRamp).toFixed(3);
        if (tintOp !== wTintOp) {
          floorLayer.tintEl.style.opacity = tintOp;
          wTintOp = tintOp;
        }
      }
      causticsUniforms.uFloorT.value = 0;
      // Warm sand light tracks the sand line exactly: the sand sits 42vh
      // below the photo's top edge; top edge is at (66 - offset)vh, so
      // sandTop = 72*P - 48 (enters the screen at P ≈ 0.67).
      const sandTopVh = 72 * floorPSm - 48;
      causticsUniforms.uFloorTop.value = Math.max(0.06, sandTopVh / 100);
      causticsUniforms.uFloorGlow.value =
        Math.min(Math.max((sandTopVh + 4) / 12, 0), 1);
    } else {
      causticsUniforms.uFloorT.value = floorPSm;
      causticsUniforms.uFloorGlow.value = 0;
    }
    // Caustics render throttled to every 2nd frame (30Hz) — every 3rd read
    // as laggy water light. Perf-mode static frames still render whenever
    // the region moves.
    const regionMoved = Math.abs(regionTop - causticsLastRegion) > 0.004
      || Math.abs(floorPSm - causticsLastFloor) > 0.01;
    const wantRender = !performanceMode
      ? (frameIdx % 2 === 0 || !causticsHasFrame)
      : (!causticsHasFrame || regionMoved);
    if (diveT > 0.01 && wantRender) {
      causticsUniforms.uTime.value = getSceneTime();
      causticsRenderer.render(causticsScene, causticsCamera);
      causticsHasFrame = true;
      causticsLastRegion = regionTop;
      causticsLastFloor = floorPSm;
    }
  }

  // --- Waterline: only visible when the camera is near the surface ---
  const surfaceDist = Math.abs(camera.position.y);
  waterlineMesh.material.opacity = surfaceDist < 10
    ? 0.12 * (1 - surfaceDist / 10)
    : 0;

  // Always render even in performance mode so scene stays visible
  renderer.render(scene, camera);
}

animate();

// ============ RESIZE ============
window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  // Refresh cached layout metrics (zone params + document height) HERE —
  // never in the scroll path — then recompute targets so a resize mid-page
  // doesn't leave the camera aimed at a stale position.
  refreshMetrics();
  updateCameraFromScroll();
}, { passive: true });

console.log('[scene] Three.js scene running — Water + gradient sky active');
